import {
    ChangeEvent,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { ThemeProvider } from "styled-components";
import { h } from "../h";
import { theme } from "../theme";
import {
    approveWearAuthorization,
    inspectWearAuthorization,
    logInWithEmail,
    registerWithEmail,
    WearAuthorizationInfo,
} from "../services/api";
import { userGet, userGetDone, userGetError } from "../actions";
import { getIsAuthorized, getUser } from "../state/user.selectors";
import { User } from "../model/user";

const Page = styled.main`
    min-height: 100vh;
    box-sizing: border-box;
    padding: 32px 18px;
    display: grid;
    place-items: center;
    color: ${({ theme }) => theme.colorMain};
    background: linear-gradient(145deg, #f7f5fb, #ebe6f4);
    font-family:
        system-ui,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
`;

const Card = styled.section`
    width: min(100%, 460px);
    box-sizing: border-box;
    padding: 34px;
    border-radius: 28px;
    background: white;
    box-shadow: 0 18px 60px rgba(43, 35, 58, 0.15);

    @media (max-width: 520px) {
        padding: 26px 20px;
        border-radius: 22px;
    }
`;

const Brand = styled.div`
    color: ${({ theme }) => theme.colorAccent};
    font-weight: 700;
    letter-spacing: 0.02em;
`;

const Heading = styled.h1`
    margin: 12px 0 8px;
    font-size: 30px;
    line-height: 1.15;
`;

const Copy = styled.p`
    margin: 8px 0 22px;
    line-height: 1.5;
    color: #645d6e;
`;

const Label = styled.label`
    display: block;
    margin: 15px 0 7px;
    font-size: 14px;
    font-weight: 600;
`;

const Input = styled.input`
    width: 100%;
    box-sizing: border-box;
    padding: 13px 14px;
    border: 1px solid #cfc7db;
    border-radius: 12px;
    font: inherit;
    outline: none;

    &:focus {
        border-color: ${({ theme }) => theme.colorAccent};
        box-shadow: 0 0 0 3px rgba(103, 80, 164, 0.15);
    }
`;

const CodeInput = styled(Input)`
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 24px;
    font-weight: 700;
`;

const Button = styled.button<{ secondary?: boolean }>`
    width: 100%;
    box-sizing: border-box;
    margin-top: 14px;
    padding: 13px 16px;
    border: 1px solid ${({ theme }) => theme.colorAccent};
    border-radius: 999px;
    color: ${({ secondary, theme }) =>
        secondary ? theme.colorAccent : `white`};
    background: ${({ secondary, theme }) =>
        secondary ? `white` : theme.colorAccent};
    font: inherit;
    font-weight: 650;
    cursor: pointer;

    &:disabled {
        cursor: default;
        opacity: 0.55;
    }
`;

const ErrorText = styled.p`
    color: ${({ theme }) => theme.colorWarning};
    line-height: 1.4;
`;

const DeviceCard = styled.div`
    margin-top: 20px;
    padding: 18px;
    border-radius: 18px;
    background: #f3eefb;
`;

const DeviceName = styled.div`
    margin-bottom: 5px;
    font-size: 21px;
    font-weight: 700;
`;

const DeviceId = styled.div`
    overflow-wrap: anywhere;
    color: #6d6676;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
`;

const Success = styled.div`
    margin-top: 20px;
    padding: 18px;
    border-radius: 18px;
    color: #163b25;
    background: #dff4e5;
    line-height: 1.45;
`;

const normalizeCode = (value: string): string =>
    value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, ``)
        .slice(0, 8);

const displayCode = (value: string): string =>
    value.length > 4 ? `${value.slice(0, 4)}-${value.slice(4)}` : value;

export const WatchAuthorization = (): ReactElement => {
    const dispatch = useDispatch();
    const isAuthorized = useSelector(getIsAuthorized);
    const user = useSelector(getUser);
    const initialCode = normalizeCode(
        new URLSearchParams(window.location.search).get(`code`) || ``,
    );
    const [code, setCode] = useState(initialCode);
    const [email, setEmail] = useState(``);
    const [password, setPassword] = useState(``);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [device, setDevice] = useState<WearAuthorizationInfo | null>(null);
    const [inspectedCode, setInspectedCode] = useState<string | null>(null);
    const [approved, setApproved] = useState(false);

    const inspect = useCallback(async () => {
        if (code.length !== 8) {
            setError(`Enter the eight-character code shown on your watch.`);
            return;
        }
        setPending(true);
        setError(null);
        setDevice(null);
        try {
            const result = await inspectWearAuthorization(code);
            setDevice(result);
            setInspectedCode(code);
        } catch (requestError) {
            setError(
                requestError instanceof Error
                    ? requestError.message
                    : `Unable to find this watch code`,
            );
            setInspectedCode(code);
        } finally {
            setPending(false);
        }
    }, [code]);

    useEffect(() => {
        if (
            isAuthorized &&
            code.length === 8 &&
            inspectedCode !== code &&
            !pending
        ) {
            void inspect();
        }
    }, [code, inspect, inspectedCode, isAuthorized, pending]);

    const authenticate = useCallback(
        async (request: (email: string, password: string) => Promise<User>) => {
            setPending(true);
            setError(null);
            dispatch(userGet());
            try {
                const authenticatedUser = await request(email, password);
                dispatch(userGetDone(authenticatedUser));
            } catch (requestError) {
                const message =
                    requestError instanceof Error
                        ? requestError.message
                        : `Authentication failed`;
                dispatch(userGetError(message));
                setError(message);
            } finally {
                setPending(false);
            }
        },
        [dispatch, email, password],
    );

    const approve = useCallback(async () => {
        setPending(true);
        setError(null);
        try {
            await approveWearAuthorization(code);
            setApproved(true);
        } catch (requestError) {
            setError(
                requestError instanceof Error
                    ? requestError.message
                    : `Unable to approve this watch`,
            );
        } finally {
            setPending(false);
        }
    }, [code]);

    const codeField = h(
        "div",
        {},
        h(Label, { htmlFor: `watch-code` }, `Watch code`),
        h(CodeInput, {
            id: `watch-code`,
            value: displayCode(code),
            maxLength: 9,
            autoComplete: `one-time-code`,
            inputMode: `text`,
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
                const nextCode = normalizeCode(event.target.value);
                setCode(nextCode);
                setDevice(null);
                setInspectedCode(null);
                setApproved(false);
                const url = new URL(window.location.href);
                if (nextCode)
                    url.searchParams.set(`code`, displayCode(nextCode));
                else url.searchParams.delete(`code`);
                window.history.replaceState(null, ``, url);
            },
        }),
    );

    return h(
        ThemeProvider,
        { theme },
        h(
            Page,
            {},
            h(
                Card,
                {},
                h(Brand, {}, `PLAN ON TODAY`),
                h(Heading, {}, `Set up your watch`),
                h(
                    Copy,
                    {},
                    isAuthorized
                        ? `Review the requesting watch before granting read-only access to your plan.`
                        : `The code stays here while you sign in. Your watch never receives your email or password.`,
                ),
                codeField,
                !isAuthorized &&
                    h(
                        "div",
                        {},
                        h(Label, { htmlFor: `watch-email` }, `Email`),
                        h(Input, {
                            id: `watch-email`,
                            type: `email`,
                            autoComplete: `email`,
                            value: email,
                            onChange: (event: ChangeEvent<HTMLInputElement>) =>
                                setEmail(event.target.value),
                        }),
                        h(Label, { htmlFor: `watch-password` }, `Password`),
                        h(Input, {
                            id: `watch-password`,
                            type: `password`,
                            autoComplete: `current-password`,
                            value: password,
                            onChange: (event: ChangeEvent<HTMLInputElement>) =>
                                setPassword(event.target.value),
                        }),
                        h(
                            Button,
                            {
                                disabled: pending || !email || !password,
                                onClick: () => authenticate(logInWithEmail),
                            },
                            `Sign in and continue`,
                        ),
                        h(
                            Button,
                            {
                                secondary: true,
                                disabled: pending || !email || !password,
                                onClick: () => authenticate(registerWithEmail),
                            },
                            `Create account`,
                        ),
                    ),
                isAuthorized &&
                    !device &&
                    !approved &&
                    h(
                        Button,
                        {
                            disabled: pending || code.length !== 8,
                            onClick: inspect,
                        },
                        pending ? `Checking…` : `Review watch`,
                    ),
                isAuthorized &&
                    device &&
                    !approved &&
                    h(
                        "div",
                        {},
                        h(
                            DeviceCard,
                            {},
                            h(DeviceName, {}, device.deviceName),
                            h(DeviceId, {}, device.deviceId),
                            h(
                                Copy,
                                {},
                                `Signed in as ${user?.username || user?.provider || `your account`}. Approval grants read-only schedule access to this device.`,
                            ),
                        ),
                        h(
                            Button,
                            { disabled: pending, onClick: approve },
                            pending ? `Approving…` : `Approve this watch`,
                        ),
                    ),
                approved &&
                    h(
                        Success,
                        { role: `status` },
                        h("strong", {}, `Watch approved.`),
                        h(
                            "div",
                            {},
                            `Return to your watch. It will claim its credential and sync directly.`,
                        ),
                    ),
                error && h(ErrorText, { role: `alert` }, error),
            ),
        ),
    );
};
